import GooglePlaceAutocompleteWrapper from "../ui/google-place-autocomplete"
import FieldInfoIcon from "../field-info"
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../ui/form"
import { cn, parseAttributes } from "../../lib/utils"
import {
	AddressFieldConfig,
	type Attribute,
	type FieldProps,
} from "../../types/form-types"
import { Description } from "./components/description"
import { useLocationStore } from "../../stores/use-location-store"
import { useFormStore } from "@/stores/use-form-store"
import { useLogicStore } from "@/stores/use-conditional-logic-store"
import { ErrorMessage } from "./components/error-message"

export const Address = ({ field, form, commonAttributes }: FieldProps) => {
	const { label, infoIconText, infoIconType, description, infoIconRequired } =
		commonAttributes
	const {
		cityFieldId,
		countryFieldId,
		stateFieldId,
		postalCodeFieldId,
		landmarkFieldId,
		placeholder,
	} = getAddressFieldAttributes(field.attributes)
	const { setPendingStateFieldId, setPendingState } = useLocationStore()
	const { shouldDisable } = useFormStore()
	const { hiddenFields, errors } = useLogicStore()

	if (hiddenFields[field.field_id]) return null

	return (
		<FormField
			name={field.field_id}
			control={form.control}
			render={({ field: formField }) => (
				<FormItem>
					<FormLabel>
						<div className="flex flex-col gap-1">
							<div className="flex items-center gap-1">
								<p
									className={cn(
										field.is_required &&
											"after:ml-1 after:text-red-500 after:content-['*']",
									)}
								>
									{label}
								</p>
								{infoIconRequired && (
									<FieldInfoIcon
										infoText={infoIconText}
										iconType={infoIconType}
									/>
								)}
							</div>
							{description && (
								<Description description={description} />
							)}
						</div>
					</FormLabel>
					<FormControl>
						<GooglePlaceAutocompleteWrapper
							shouldDisable={shouldDisable}
							placeholder={placeholder || "Enter a location"}
							value={formField.value}
							onValueChange={value => {
								formField.onChange(value)
							}}
							onPlaceSelect={values => {
								if (cityFieldId)
									form.setValue(cityFieldId, values.city)
								if (stateFieldId) {
									setPendingStateFieldId(stateFieldId)
									setPendingState(values.state)
								}
								if (countryFieldId)
									form.setValue(
										countryFieldId,
										values.country,
									)
								if (postalCodeFieldId)
									form.setValue(
										postalCodeFieldId,
										values.pincode,
									)
								if (landmarkFieldId)
									form.setValue(
										landmarkFieldId,
										values.landmark,
									)
								formField.onChange(values.address)
							}}
						/>
					</FormControl>
					<FormMessage />
					<ErrorMessage error={errors[field.field_id]} />
				</FormItem>
			)}
		/>
	)
}

function getAddressFieldAttributes(attributes: Attribute[]) {
	const props: {
		cityFieldId?: string
		stateFieldId?: string
		countryFieldId?: string
		postalCodeFieldId?: string
		landmarkFieldId?: string
		placeholder: string
	} = { placeholder: "" }

	const parsedAttributes = parseAttributes(attributes)

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case AddressFieldConfig.CityFieldId:
				props.cityFieldId = value as string
				break
			case AddressFieldConfig.PostalCodeFieldId:
				props.postalCodeFieldId = value as string
				break
			case AddressFieldConfig.StateFieldId:
				props.stateFieldId = value as string
				break
			case AddressFieldConfig.countryFieldId:
				props.countryFieldId = value as string
				break
			case AddressFieldConfig.LandMarkField:
				props.landmarkFieldId = value as string
				break
			case AddressFieldConfig.placeholderText:
				props.placeholder = value as string
				break
		}
	})

	return props
}
